import type { HTMLAttributes } from 'react'
import { cx, spinner } from '../../lib/ui-primitives'

// ============================================
// SPINNER COMPONENT
// Loading indicator with size variants
// ============================================

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl'

const sizeStyles: Record<SpinnerSize, string> = {
  sm: spinner.small,
  md: spinner.medium,
  lg: 'size-12 border-3 border-white/15 border-t-indigo-400',
  xl: spinner.large,
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'size-5',
  md: 'size-8',
  lg: 'size-12',
  xl: 'size-20',
}

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Size variant */
  size?: SpinnerSize
  /** Custom accent color */
  color?: string
  /** Center in container */
  centered?: boolean
  /** Accessible label */
  label?: string
  /** Optional center content (emoji, icon) */
  centerContent?: React.ReactNode
}

/**
 * Spinner - Loading indicator
 *
 * Extracted from: WelcomeScreen, ProcessingScreen, LookupScreen
 *
 * Features:
 * - 4 size presets
 * - Custom accent color
 * - Center content slot for icons/emoji
 * - Accessible with sr-only label
 */
export function Spinner({
  size = 'md',
  color,
  centered = false,
  label = 'Loading...',
  centerContent,
  className,
  style,
  ...props
}: SpinnerProps) {
  const spinnerElement = (
    <div
      className={cx(
        'relative inline-flex items-center justify-center',
        sizeClasses[size]
      )}
      role="status"
      aria-label={label}
      {...props}
    >
      <div
        className={cx(
          'absolute inset-0 rounded-full animate-spin',
          sizeStyles[size],
          className
        )}
        style={{
          ...(color && { borderTopColor: color }),
          ...style,
        }}
      />
      {centerContent && (
        <span
          className="relative z-10 flex items-center justify-center"
          aria-hidden="true"
        >
          {centerContent}
        </span>
      )}
      <span className="sr-only">{label}</span>
    </div>
  )

  if (centered) {
    return (
      <div className="flex items-center justify-center">
        {spinnerElement}
      </div>
    )
  }

  return spinnerElement
}

// ============================================
// LOADING STATE COMPONENT
// Full loading overlay with spinner and message
// ============================================

export interface LoadingStateProps {
  /** Loading message */
  message?: string
  /** Sub-message */
  subMessage?: string
  /** Spinner size */
  size?: SpinnerSize
  /** Center content for spinner */
  icon?: React.ReactNode
}

/**
 * LoadingState - Full loading overlay
 *
 * Extracted from: ProcessingScreen (verifying payment state)
 */
export function LoadingState({
  message = 'Loading...',
  subMessage,
  size = 'xl',
  icon,
}: LoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="mb-6 sm:mb-8">
        <Spinner size={size} centerContent={icon} />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
        {message}
      </h2>
      {subMessage && (
        <p className="text-white/55 text-sm sm:text-base">
          {subMessage}
        </p>
      )}
    </div>
  )
}
